'use client';

import React, {useEffect, useRef} from 'react';
import classNames from 'classnames';
import {publishEvent} from '@udemy-v2/event-tracking';
import {QuizProperties} from './quiz.types';
import {useQuiz} from './hooks/use-quiz';
import {DiscoveryItemClickEvent} from '../events';
import styles from './quiz.module.scss';

const QUIZ_PROPERTIES: QuizProperties = {
    id: '686eb4c1a141560fb5267d00',
    title: 'What kind of hustler are you?',
    url: 'https://udemy581.outgrow.us/686eb4c1a141560fb5267d00?vHeight=1',
    width: '100%',
    loaderSrc: 'https://dyv6f9ner1ir9.cloudfront.net/assets/js/nloader.js',
};

export function Quiz(): React.ReactNode {
    const {status, containerRef, hiddenUntilReady} = useQuiz(QUIZ_PROPERTIES);
    const hasTrackedInteraction = useRef(false);

    useEffect(() => {
        if (status !== 'ready') return;

        // Capture the current container reference to avoid stale closure issues
        const container = containerRef.current;
        let overlay: HTMLDivElement | null = null;
        let originalPosition: string | null = null;

        const handleQuizInteraction = () => {
            if (!hasTrackedInteraction.current) {
                hasTrackedInteraction.current = true;

                publishEvent(
                    new DiscoveryItemClickEvent({
                        type: 'simple_quiz',
                        id: 0,
                        trackingId: crypto.randomUUID(),
                        backendSource: 'discovery',
                        position: 0,
                        uiRegion: 'quiz',
                        pageFor: '60dayhustle',
                    }),
                );
            }
        };

        const timer = setTimeout(() => {
            const iframe = container?.querySelector('iframe');
            if (iframe && container) {
                overlay = document.createElement('div');
                overlay.style.cssText = `
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: transparent;
                    cursor: pointer;
                    z-index: 1;
                `;

                overlay.addEventListener('click', (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleQuizInteraction();

                    if (overlay && container) {
                        container.removeChild(overlay);
                        overlay = null;

                        // Restore original position if it was changed
                        if (originalPosition !== null) {
                            container.style.position = originalPosition;
                            originalPosition = null;
                        }
                    }
                });

                const computedStyle = window.getComputedStyle(container);
                const currentPosition = computedStyle.position;

                if (currentPosition === 'static') {
                    originalPosition = container.style.position || 'static';
                    container.style.position = 'relative';
                }

                container.appendChild(overlay);
            }
        }, 1000);

        return () => {
            clearTimeout(timer);

            if (overlay && container) {
                try {
                    container.removeChild(overlay);
                } catch {
                    // Overlay may have already been removed
                }
                overlay = null;
            }

            if (originalPosition !== null && container) {
                container.style.position = originalPosition;
                originalPosition = null;
            }
        };
    }, [status, containerRef]);

    if (status === 'error') return null;

    return (
        <div
            className={styles['quiz-container']}
            style={hiddenUntilReady ? {display: 'none'} : undefined}
        >
            <div
                ref={containerRef}
                className={classNames(styles.quiz, 'op-interactive')}
                id={QUIZ_PROPERTIES.id}
                data-title={QUIZ_PROPERTIES.title}
                data-url={QUIZ_PROPERTIES.url}
                data-width={QUIZ_PROPERTIES.width}
            />
        </div>
    );
}
